import { useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { errorMessage } from '@/components/Toast';
import { Button, Field, Input } from '@/components/ui';
import { AuthAside } from './AuthAside';

export function LoginPage() {
  const { status, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string })?.from ?? '/';

  const [usernameOrEmail, setU] = useState('');
  const [password, setP] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (status === 'authenticated') return <Navigate to={from} replace />;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(usernameOrEmail.trim(), password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth">
      <AuthAside />
      <div className="auth-main">
        <div className="auth-card">
          <p className="eyebrow">Welcome back</p>
          <h1>Sign in to ALFIE</h1>

          <form className="stack gap-4" onSubmit={onSubmit}>
            <Field label="Username or email">
              <Input value={usernameOrEmail} onChange={(e) => setU(e.target.value)} autoComplete="username" placeholder="you@firm.com" required autoFocus />
            </Field>
            <Field label="Password" error={error ?? undefined}>
              <Input type="password" value={password} onChange={(e) => setP(e.target.value)} autoComplete="current-password" placeholder="••••••••" required />
            </Field>
            <Button type="submit" className="block" loading={busy}>Sign in</Button>
          </form>

          <div className="auth-foot stack gap-2">
            <span>
              No account yet? <Link to="/register" className="inline-link">Create one</Link> — it takes a minute,
              and chat works without one.
            </span>
            <span>
              Forgotten your password? <Link to="/forgot-password" className="inline-link">Reset it by email</Link>.
            </span>
            <span className="muted-more t-xs">
              The first account created on a new workspace becomes its administrator.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
